import React from 'react'
import { compose } from 'recompose'

import {
  FormData,
  withFormCollector,
} from '../../../src/components/forms/FormCollector'
import {
  BasicType,
  Column,
  Field,
} from '../../../src/components/forms/FormElementParts'
import styled, { MQ_TABLET, Theme } from '../../../src/constants/theme'
import FooterDiv from '../elements/FooterDiv'
import FieldFactory, {
  Form,
  getModalOpenName,
  JustifiedDiv,
  replaceLabel,
  ServerError,
} from './FieldFactory'

interface State {
  [key: string]: boolean
}

interface Props {
  form: Form
  formData: FormData
  isValid: boolean
  onChange?: (formData: FormData) => void
  onClickBack: () => void
  onReplaceLabel?: (label: string) => string
  onSubmit: (formData: FormData) => void
  serverError: ServerError
}

class FormBuilder extends React.Component<Props> {
  constructor(props: Props) {
    super(props)
    const {
      form: { fields },
    } = this.props
    const initState = {}
    fields.forEach(field => {
      if (field.hint) {
        initState[getModalOpenName(field)] = false
      }
    })
    this.state = initState
  }

  componentDidMount() {
    const focusable: any = document.querySelector('input, select')
    if (focusable) {
      focusable.focus()
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return (
      nextState !== this.state || // allow dynamic modals to open and close
      ['form', 'formData', 'isValid', 'serverError'].some(
        prop => this.props[prop] !== nextProps[prop],
      )
    )
  }

  onClickBack = () => {
    this.props.onClickBack()
  }

  onCloseModal = (field: Field) => () => {
    this.setState({ [getModalOpenName(field)]: false })
  }

  onOpenModal = (field: Field) => () => {
    this.setState({ [getModalOpenName(field)]: true })
  }

  onSubmitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()
    const { formData, onSubmit } = this.props
    onSubmit(formData)
  }

  render() {
    const {
      form,
      isValid,
      onChange,
      onClickBack,
      onReplaceLabel,
      serverError,
    } = this.props
    const { fields, header } = form
    const { message } = serverError
    const footerFields = fields.filter(
      (field: Field) => field.type === BasicType.FOOTER,
    )

    return (
      <React.Fragment>
        <Contents className="FormBuilder">
          {header && (
            <FormHeader>
              <h1>{replaceLabel(header, onReplaceLabel)}</h1>
            </FormHeader>
          )}
          {message && (
            <FormError>
              <JustifiedDiv
                className="errorMessage"
                dangerouslySetInnerHTML={{
                  __html: message,
                }}
              />
            </FormError>
          )}
          <FormEl onSubmit={this.onSubmitForm}>
            <Column>
              <FieldFactory
                fields={fields}
                formType={form.type}
                isValid={isValid}
                onChange={onChange}
                onClickBack={onClickBack}
                onCloseModal={this.onCloseModal}
                onOpenModal={this.onOpenModal}
                onReplaceLabel={onReplaceLabel}
                serverError={serverError}
                state={this.state}
              />
            </Column>
          </FormEl>
        </Contents>
        {footerFields &&
          footerFields.length > 0 &&
          footerFields.map((field: Field) => (
            <FooterDiv
              className={field.type}
              dangerouslySetInnerHTML={{
                __html: field.label,
              }}
              key={field.label}
            />
          ))}
      </React.Fragment>
    )
  }
}

const Contents = styled.div(
  {
    maxWidth: '100%',
    overflow: 'hidden',
    width: 500,
  },
  ({ theme }) => ({
    background: theme.background,
    borderRadius: theme.borderRadius,
    color: theme.text,
  }),
)

const sharedContentThemeStyles = (theme: Theme) => ({
  ...MQ_TABLET({
    padding: theme.padding,
  }),
  padding: theme.paddingMobile,
})

const FormHeader = styled.div(
  {
    '& h1': {
      fontSize: 18,
      fontWeight: 'bold',
    },
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  ({ theme }) => ({
    ...sharedContentThemeStyles(theme),
    background: theme.greyLight,
  }),
)

const FormError = styled.div(
  {
    '& > div': {
      marginBottom: 0,
    },
  },
  ({ theme }) => sharedContentThemeStyles(theme),
)

const FormEl = styled.form({}, ({ theme }) => sharedContentThemeStyles(theme))

export default compose<any, any>(withFormCollector)(FormBuilder)
